"use client";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";
import { useCardSearch } from "modules/cards/stores/useCardsStore";
import { TailwindCardAutocomplete } from "modules/common/components/autocomplete/TailwindCardAutocomplete";
import { cn, getCardUrl } from "utils/utils";
import { useCardAutocomplete } from "modules/decks/hooks/useCardAutocomplete";
import Link from "next/link";
import { AutocompleteGameCard } from "modules/cards/model/card";
import { AutocompleteListItem } from "modules/common/components/autocomplete/AutocompleteListItem";
import { useRouter } from "@bprogress/next/app";
import { useDefaultGame } from "modules/common/hooks/useDefaultGame";
import type { GameId } from "modules/common/model/models";
import { primaryAvailableFinish } from "modules/cards/model/finishBehavior";

export interface InlineSearchRef {
  focus: () => void;
}

interface InlineSearchProps {
  tooltipPlacement?: "top" | "right" | "bottom" | "left";
  className?: string;
  game?: GameId;
}

export const InlineSearch = forwardRef<InlineSearchRef, InlineSearchProps>(
  function InlineSearch(
    { tooltipPlacement = "right", className, game: gameProp },
    ref
  ) {
    const { hasSingleGame, defaultGame } = useDefaultGame();
    const game =
      gameProp ?? (hasSingleGame && defaultGame ? defaultGame : undefined);
    const router = useRouter();
    const {
      searchRequest: req,
      setSearchRequest: setReq,
      resetSearchRequest: resetReq,
    } = useCardSearch();
    const { query, loadMoreRef, scrollContainerRef, opts } =
      useCardAutocomplete(req);
    const inputRef = useRef<HTMLInputElement>(null);

    // Expose focus method to parent components
    useImperativeHandle(ref, () => ({
      focus: () => {
        inputRef.current?.focus();
      },
    }));

    // Direct keyboard shortcut handling - much simpler than context providers!
    useEffect(() => {
      const handleKeyPress = (event: KeyboardEvent) => {
        // Handle "/" key for search focus
        if (event.key === "/" && inputRef.current) {
          // Don't trigger if user is already typing in an input/textarea
          if (
            event.target instanceof HTMLInputElement ||
            event.target instanceof HTMLTextAreaElement ||
            (event.target as HTMLElement)?.contentEditable === "true"
          ) {
            return;
          }

          // Only focus if the input is actually visible (not hidden via CSS)
          const isVisible = inputRef.current.offsetParent !== null;
          if (!isVisible) {
            return;
          }

          event.preventDefault();
          inputRef.current?.focus();
        }

        // Handle "Escape" key to blur focus
        if (
          event.key === "Escape" &&
          inputRef.current &&
          document.activeElement === inputRef.current
        ) {
          event.preventDefault();
          inputRef.current.blur();
        }
      };

      // Add event listener when the component mounts
      document.addEventListener("keydown", handleKeyPress);

      // Clean up the event listener when the component unmounts
      return () => {
        document.removeEventListener("keydown", handleKeyPress);
      };
    }, []);

    const handleInputChange = useCallback(
      (value: string) => {
        if (value.length < 3) {
          return;
        }
        setReq({ string: value, game }); // Inline search is currently MTG only
      },
      [game, setReq]
    );

    const handleClear = useCallback(() => {
      resetReq();
    }, [resetReq]);

    const handleSelect = useCallback(
      (item: AutocompleteGameCard) => {
        resetReq();
        if (!item?.game || !item?.setCode) return;
        router.push(getCardUrl(item, primaryAvailableFinish(item.finishes)));
      },
      [resetReq, router]
    );

    const renderItem = useCallback(
      (item: AutocompleteGameCard, props: any) => {
        return (
          <Link
            href={
              item?.game && item?.setCode
                ? getCardUrl(item, primaryAvailableFinish(item.finishes))
                : "#"
            }
            onClick={resetReq}
          >
            <AutocompleteListItem
              option={item}
              highlighted={props.highlighted}
              itemProps={props}
            />
          </Link>
        );
      },
      [resetReq]
    );

    return (
      <div className={cn("max-w-xs", className)}>
        <TailwindCardAutocomplete
          onInputChange={handleInputChange}
          onClear={handleClear}
          options={opts()}
          isLoading={query.isLoading}
          isFetchingNextPage={query.isFetchingNextPage}
          loadMoreRef={loadMoreRef}
          scrollContainerRef={scrollContainerRef}
          error={query.error}
          onSelect={handleSelect}
          renderItem={renderItem}
          tooltipPlacement={tooltipPlacement}
          inputRef={inputRef}
          keyboardShortcut={<kbd className="kbd">/</kbd>}
        />
      </div>
    );
  }
);
